/**
 * AdminExcludedIPs - Manage excluded IPs from visitor tracking
 */
import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Shield,
  Plus,
  Trash2,
  Users,
  Globe,
  RefreshCw,
  Search,
  AlertCircle
} from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminExcludedIPs = () => {
  const [excludedIPs, setExcludedIPs] = useState([]);
  const [userIPs, setUserIPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newIP, setNewIP] = useState("");
  const [newReason, setNewReason] = useState("");
  const [stats, setStats] = useState({ manual: 0, auto: 0 });
  const [activeTab, setActiveTab] = useState("manual");

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [excludedRes, userIPsRes] = await Promise.all([
        axios.get(`${API}/api/admin/excluded-ips`, { headers }),
        axios.get(`${API}/api/admin/user-ips`, { headers })
      ]);

      setExcludedIPs(excludedRes.data.excluded_ips || []);
      setUserIPs(userIPsRes.data.users || []);
      setStats({
        manual: excludedRes.data.manual_count || 0,
        auto: excludedRes.data.auto_excluded_users || 0
      });
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addExcludedIP = async (e) => {
    e.preventDefault();
    if (!newIP.trim()) return;

    // Basic IP validation
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (!ipRegex.test(newIP.trim())) {
      toast.error("Formato de IP invalido");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/api/admin/excluded-ips`,
        { ip: newIP.trim(), reason: newReason.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`IP ${newIP} excluida`);
      setNewIP("");
      setNewReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar IP");
    }
  };

  const removeExcludedIP = async (ip) => {
    if (!confirm(`Eliminar ${ip} de la lista de exclusion?`)) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/api/admin/excluded-ips/${ip}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`IP ${ip} eliminada`);
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar IP");
    }
  };

  const removeUserIP = async (userId, ip) => {
    if (!confirm(`Permitir que ${ip} sea rastreada nuevamente?`)) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/api/admin/user-ips/${userId}/${ip}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`IP ${ip} eliminada del usuario`);
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar IP");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-navy-800 flex items-center gap-2">
              <Shield className="w-7 h-7 text-blue-500" />
              IPs Excluidas del Tracking
            </h1>
            <p className="text-navy-500 text-sm mt-1">
              Gestiona que IPs no aparecen como visitantes en el panel EN VIVO
            </p>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy-800">{stats.manual}</p>
                <p className="text-sm text-navy-500">IPs Manuales</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy-800">{stats.auto}</p>
                <p className="text-sm text-navy-500">Usuarios con IPs</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-navy-200 pb-2">
          <button
            onClick={() => setActiveTab("manual")}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
              activeTab === "manual"
                ? "bg-blue-500 text-white"
                : "text-navy-600 hover:bg-navy-100"
            }`}
          >
            IPs Manuales ({excludedIPs.length})
          </button>
          <button
            onClick={() => setActiveTab("auto")}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
              activeTab === "auto"
                ? "bg-emerald-500 text-white"
                : "text-navy-600 hover:bg-navy-100"
            }`}
          >
            IPs de Usuarios ({userIPs.length})
          </button>
        </div>

        {/* Manual IPs Tab */}
        {activeTab === "manual" && (
          <div className="space-y-4">
            {/* Add IP Form */}
            <form onSubmit={addExcludedIP} className="bg-white rounded-xl border border-navy-100 p-4">
              <h3 className="font-medium text-navy-800 mb-3">Agregar IP a Excluir</h3>
              <div className="flex gap-3">
                <Input
                  type="text"
                  placeholder="Ej: 192.168.1.1"
                  value={newIP}
                  onChange={(e) => setNewIP(e.target.value)}
                  className="flex-1"
                />
                <Input
                  type="text"
                  placeholder="Razon (opcional)"
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                  className="flex-1"
                />
                <Button type="submit" className="bg-blue-500 hover:bg-blue-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar
                </Button>
              </div>
              <p className="text-xs text-navy-400 mt-2">
                Las IPs agregadas aqui no apareceran en el panel de visitantes EN VIVO
              </p>
            </form>

            {/* IP List */}
            <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
              {excludedIPs.length === 0 ? (
                <div className="p-8 text-center text-navy-400">
                  <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay IPs excluidas manualmente</p>
                </div>
              ) : (
                <div className="divide-y divide-navy-100">
                  {excludedIPs.map((item, idx) => (
                    <motion.div
                      key={item.ip}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex items-center justify-between p-4 hover:bg-navy-50"
                    >
                      <div>
                        <p className="font-mono font-medium text-navy-800">{item.ip}</p>
                        <p className="text-xs text-navy-500">
                          {item.reason || "Sin razon"} - Agregado por {item.added_by_name}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeExcludedIP(item.ip)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Auto IPs Tab (Users) */}
        {activeTab === "auto" && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="text-sm text-amber-800 font-medium">IPs Automaticas</p>
                  <p className="text-xs text-amber-700">
                    Estas IPs se excluyen automaticamente porque pertenecen a usuarios que han iniciado sesion.
                    Si eliminas una IP, esa direccion podra ser rastreada nuevamente como visitante.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
              {userIPs.length === 0 ? (
                <div className="p-8 text-center text-navy-400">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay usuarios con IPs registradas</p>
                </div>
              ) : (
                <div className="divide-y divide-navy-100">
                  {userIPs.map((user, idx) => (
                    <motion.div
                      key={user.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.03 }}
                      className="p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium text-navy-800">{user.name || user.email}</p>
                          <p className="text-xs text-navy-500">{user.role} - {user.email}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {user.known_ips?.map((ip) => (
                          <div
                            key={ip}
                            className="flex items-center gap-2 bg-navy-100 rounded-lg px-3 py-1"
                          >
                            <span className="font-mono text-sm text-navy-700">{ip}</span>
                            <button
                              onClick={() => removeUserIP(user.id, ip)}
                              className="text-navy-400 hover:text-red-500"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminExcludedIPs;
